/* LogsViewRenderer.js */

"use strict";
const ComboBoxControl = require("@gluenetworks/steve_ui_core").Control.OptionSelection.Single
    .ComboBox;
const Registrations = require("../../../../../../../RegistrationNamespaces");
const _ = require("lodash");
const BaseLogsViewRenderer = require("../../../../../../lib/logs/BaseLogsViewRenderer");
const FileName = require("@gluenetworks/steve_utilities").fileName;

class LogsViewRenderer extends BaseLogsViewRenderer {
    constructor(model) {
        super(model);
        this._rootElem = document.createElement("nd-results-log-view-gl");
        this._rootElem.classList.add("logs-control-root");
        this._rootElem.component = this._model;
        this._planResultsList = new ComboBoxControl({
            options: this._getDevices(),
            placeholder: "Select Device",
            value: this._model._networkDiscoveryResultEntity,
        });
        this._rootElem.appendChild(this._planResultsList.renderer.rootElement);
        this._rootElem.appendChild(this._logsControl.renderer.getRootElement());
        this._logFile = "";
        this._logFileStarttime = undefined;
        this._onDownloadButtonClick = this._onDownloadButtonClick.bind(this);
        this._onBackButtonClick = this._onBackButtonClick.bind(this);
        this._allDeviceLabel = document.createElement("label");
        this._actionsRegistered = false;
        this._fileNamer = FileName;

        this._registerListeners();
        this._resizeLogsControl();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._logFile = "";
            this._logFileStarttime = undefined;
            this._fileNamer = undefined;
            super._shutdown().then(() => {
                this._rootElem.remove();
                resolve();
            });
        });
        return prom;
    }

    _registerViewActions() {
        super._registerViewActions();
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Back",
            name: "back",
            handler: this._onBackButtonClick,
        });

        if (this._model._logFeed !== undefined) {
            this._model.gameEditor.addViewAction({
                type: "normal",
                displayName: "Download",
                name: "cancel",
                handler: this._onDownloadButtonClick,
            });
        }
    }

    _registerListeners() {
        super._registerListeners();
        this._planResultsList.on(
            "UserValue",
            (this._switchResults = this._switchResults.bind(this))
        );
        this._model.on("LogsAdded", (this._onLogsAdded = this._onLogsAdded.bind(this)));
        this._model.on(
            "LogsInitialized",
            (this._onLogsInitialized = this._onLogsInitialized.bind(this))
        );
    }

    _unregisterListeners() {
        super._unregisterListeners();
        this._planResultsList.removeAllListeners("UserValue");
        this._model.removeAllListeners("LogsAdded");
        this._model.removeAllListeners("LogsInitialized");
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */

    render() {
        this._model.whenModelIsReady().then(() => {
            if (this._model._logFeed === undefined) {
                this._showDefaultMessageDisplay();
            }
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onLogsInitialized(logs) {
        this._appendLogs(logs);
    }

    _onLogsAdded(logs) {
        this._appendLogs(logs);
    }

    dataToInt8Arr(data) {
        const byteString = window.atob(data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const int8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            int8Array[i] = byteString.charCodeAt(i);
        }
        return int8Array;
    }

    _onDownloadButtonClick() {
        if (!_.isEmpty(this._logFile)) {
            this.model.gameEditor.disableViewAction("cancel");
            this._model._modalUserFeedback
                .displayLoading({message: "Download in progress..."})
                .then(() => {
                    return this.model.retrieveJob();
                })
                .then((jobEntity) => {
                    return jobEntity.runJobBundleWork();
                })
                .then((data) => {
                    const fileData = _.get(data, "summary.bundle", "");
                    return this.model.io.fileFactory.saveArrayBufferAsFile(
                        this.dataToInt8Arr(fileData),
                        this._fileNamer({
                            solution: "DM",
                            general: "NetworkDiscoveryActivity",
                            specific: this._planResultsList.value.deviceName,
                            date: this._logFileStarttime,
                            suffix: "zip",
                        }),
                        "application/zip"
                    );
                })
                .catch((error) => {
                    this._model._logger.warn(
                        {error},
                        "Network Discovery onDownloadButtonClick - error"
                    );
                    this._model._modalUserFeedback.hideDisplayed().then(() => {
                        this._model._modalUserFeedback.displayError(error);
                    });
                    return Promise.reject(error);
                })
                .then(() => {
                    return this._model._modalUserFeedback.hideDisplayed();
                })
                .finally(() => {
                    this.model.gameEditor.enableViewAction("cancel");
                    return Promise.resolve();
                });
        }
    }

    _onBackButtonClick() {
        this.model.gameEditor.setActiveView(Registrations.NETWORK_DISCOVERY_EDITOR_VIEW_RESULTS);
    }

    _switchResults(value) {
        this._logFile = "";
        this._logFileStarttime = undefined;
        this._logsControl.setLogs([]);
        this._showDefaultMessageDisplay();
        this._model.switchResults(value);
    }

    _showDefaultMessageDisplay() {
        this._model.gameEditor
            .showDefaultMessageView({
                message: "No device logs available",
            })
            .then(() => {
                this._defaultViewActive = true;
                this._rootElem.appendChild(
                    this._model._gameEditor._defaultMessageViewCmpnt._renderer.getRootElement()
                );
            });
    }

    _hideDefaultMessageDisplay() {
        this._model.gameEditor.hideDefaultMessageView().then(() => {
            this._defaultViewActive = false;
        });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _getDevices() {
        const deviceList = Object.values(
            _.get(this._model._networkDiscoveryResultFeed, "cache", {})
        );

        return deviceList.map((device) => {
            return {
                label:
                    _.get(device, "deviceName", false) ||
                    _.get(device, "connectionInformation.ip", "Unknown"),
                value: device,
            };
        });
    }

    _appendLogs(logs) {
        if (!this._actionsRegistered) {
            this._registerViewActions();
        }
        this._actionsRegistered = true;
        this._logsControl.appendLogs(logs);
        this._appendLogFileLine(logs);
    }

    _appendLogFileLine(logs) {
        let logsArray = _.isArray(logs) ? logs : [logs];
        for (let i = 0; i < logsArray.length; i++) {
            let logFileLine = this._createLogFileLine(logsArray[i]);
            this._logFile += logFileLine.join(" ") + "\n";
        }
    }

    _createLogFileLine(log) {
        let logFileLine = [];
        logFileLine.push(_.get(log, "lln", ""));
        let timestamp = _.get(log, "time", undefined);
        let fileTimeStamp = timestamp;

        if (!_.isUndefined(timestamp)) {
            fileTimeStamp = Date.create(timestamp).format("{yyyy}-{MM}-{dd}T{HH}:{mm}:{ss}.{fff}");
            timestamp = Date.create(timestamp).format("{HH}:{mm}:{ss}.{fff}");
        } else {
            timestamp = "";
        }
        logFileLine.push(timestamp);

        if (!this._logFileStarttime) {
            this._logFileStarttime = fileTimeStamp;
        }

        let lvl = "";
        if (_.get(log, "resp", "")) {
            lvl = "RESP";
        } else if (_.get(log, "eventType", false)) {
            if (_.get(log, "eventType") === "START" || _.get(log, "eventType") === "FINISH") {
                lvl = _.get(log, "eventType");
            } else {
                lvl = "EVENT";
            }
        }
        logFileLine.push(lvl);

        let msg = _.get(log, "msg", _.get(log, "resp", ""));
        logFileLine.push(msg);

        return logFileLine;
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = LogsViewRenderer;
